/**
 * Set of `ui.component` values that indicate a custom parameter is a file
 * upload. Mirrors every file-upload component declared in the backend
 * `customParamSchema.ui.component` enum. File-typed params are surfaced as
 * ghost cards in the chat input's attachment row (see `FileParamGhostCard` /
 * `ChatInputFilesContainer`) rather than as a button on the bottom parameter
 * bar, so this list is also used to filter them out of the bottom bar.
 *
 * NOTE: keep in sync with backend `inferencing/utils/file_param_resolver.js`.
 */
export const FILE_COMPONENT_TYPES = new Set([
  'file',
  'fileMultiple',
  'image',
  'imageMultiple',
  'multiImageUpload',
  'imageMask',
  'audio',
  'audioWaveform',
  'video',
  'videoTrimmer',
  'pointCloud',
  'layerViewer',
]);

/**
 * Per-component icon override for the ghost card. Falls back to the
 * `validation.file.fileType` mapping inside the card itself (and ultimately
 * to a generic file icon) when a component isn't listed here.
 */
export const FILE_COMPONENT_ICON = {
  fileMultiple: 'files',
  imageMultiple: 'images',
  multiImageUpload: 'images',
  imageMask: 'mask',
  audioWaveform: 'waveform',
  videoTrimmer: 'film',
  pointCloud: 'cubes-stacked',
  layerViewer: 'layer-group',
};

export const isFileParam = (param) => FILE_COMPONENT_TYPES.has(param?.ui?.component);

/**
 * Components that can be promoted to the inline (in-composer) slot via
 * `ui.displayPosition === 'inline'`. Restricted to free-text inputs so the
 * "stage" slot stays semantically clear — chips/toggles/sliders belong on
 * the bottom bar where they're compact.
 */
const INLINE_COMPONENT_TYPES = new Set(['text', 'textarea']);

/**
 * True for params the chat composer should render as a dedicated row
 * above the main textarea (instead of as a chip on the bottom bar).
 * Author opt-in via `ui.displayPosition === 'inline'`, gated to text-y
 * components.
 */
export const isInlineParam = (param) =>
  param?.ui?.displayPosition === 'inline'
  && INLINE_COMPONENT_TYPES.has(param?.ui?.component);

/**
 * True for params explicitly hidden from the bottom bar (and surfaced only
 * in the "all parameters" modal). Gives authors a way to keep advanced
 * knobs out of the composer chrome without removing them entirely.
 */
export const isModalOnlyParam = (param) =>
  param?.ui?.displayPosition === 'modal';

/**
 * Convenience: true if a param is "non-bar" — either rendered as a ghost
 * card, an inline row, or hidden in the modal — and therefore should be
 * filtered out of the bottom-bar parameter scroller.
 */
export const isNonBarParam = (param) =>
  isFileParam(param) || isInlineParam(param) || isModalOnlyParam(param);
